import React, { useState } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  LayoutDashboard,
  Users,
  TrendingUp,
  FileCheck,
  DollarSign,
  Activity,
  LogOut,
  Menu,
  ChevronRight,
  Shield,
  Home,
} from 'lucide-react';

/**
 * Admin paneli navigasyon öğeleri
 */
const navItems = [
  {
    name: 'Dashboard',
    path: '/admin',
    icon: LayoutDashboard,
  },
  {
    name: 'Kullanıcılar',
    path: '/admin/users',
    icon: Users,
  },
  {
    name: 'Yatırımlar',
    path: '/admin/investments',
    icon: TrendingUp,
  },
  {
    name: 'Belgeler',
    path: '/admin/documents',
    icon: FileCheck,
  },
  {
    name: 'İşlemler',
    path: '/admin/transactions',
    icon: DollarSign,
  },
  {
    name: 'Aktiviteler',
    path: '/admin/activities',
    icon: Activity,
  },
];

/**
 * Sayfa başlığını pathname'den çıkaran yardımcı fonksiyon
 */
function getPageTitle(pathname) {
  const exactMatch = navItems.find((item) => item.path === pathname);
  if (exactMatch) return exactMatch.name;

  const parentMatch = navItems
    .filter((item) => item.path !== '/admin')
    .find((item) => pathname.startsWith(item.path));
  if (parentMatch) return parentMatch.name;

  return 'Dashboard';
}

/**
 * Breadcrumb oluşturan yardımcı fonksiyon
 */
function getBreadcrumbs(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  const crumbs = [{ label: 'Admin Panel', path: '/admin' }];

  if (segments.length > 1) {
    const currentTitle = getPageTitle(pathname);
    if (currentTitle !== 'Dashboard') {
      const parentItem = navItems.find((item) =>
        pathname.startsWith(item.path) && item.path !== '/admin'
      );
      if (parentItem) {
        crumbs.push({ label: parentItem.name, path: parentItem.path });
      }
      // Alt sayfa varsa (örn: /admin/users/:id)
      if (segments.length > 2) {
        crumbs.push({ label: 'Detay', path: pathname });
      }
    }
  }

  return crumbs;
}

/**
 * Sidebar navigasyon bileşeni
 */
function SidebarNav({ onItemClick }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, logout } = useAuth();

  const isActivePath = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/tr/giris');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Logo / Marka */}
      <div className="p-6 border-b border-white/10">
        <Link to="/tr" className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-red-600 rounded flex items-center justify-center text-white font-bold text-sm">
            <Shield size={16} />
          </div>
          <span className="text-xl font-heading text-white">OlyInvestment</span>
        </Link>
        <p className="text-sm text-red-300/80 mt-2 flex items-center gap-1">
          <Shield size={12} />
          Admin Panel
        </p>
      </div>

      {/* Navigasyon Linkleri */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActivePath(item.path);

          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onItemClick}
              className={cn(
                'flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200',
                active
                  ? 'bg-red-600 text-white shadow-lg'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              )}
            >
              <Icon size={20} />
              <span className="font-medium">{item.name}</span>
            </Link>
          );
        })}

        {/* Ayırıcı */}
        <div className="border-t border-white/10 my-3" />

        {/* Siteye Dön */}
        <Link
          to="/tr"
          onClick={onItemClick}
          className="flex items-center space-x-3 px-4 py-3 rounded-lg text-white/60 hover:bg-white/10 hover:text-white transition-all duration-200"
        >
          <Home size={20} />
          <span className="font-medium">Siteye Dön</span>
        </Link>
      </nav>

      {/* Kullanıcı Bilgisi + Çıkış */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center space-x-3 px-4 py-3 mb-2">
          <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white font-semibold">
            {profile?.full_name?.charAt(0)?.toUpperCase() || 'A'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {profile?.full_name || 'Admin'}
            </p>
            <p className="text-xs text-white/60 truncate">Yönetici</p>
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={handleLogout}
          className="w-full flex items-center justify-start space-x-3 px-4 py-3 rounded-lg text-white/80 hover:bg-red-500/20 hover:text-red-300"
        >
          <LogOut size={20} />
          <span className="font-medium">Çıkış Yap</span>
        </Button>
      </div>
    </div>
  );
}

/**
 * AdminLayout – Admin paneli ana layout bileşeni
 *
 * Sol sidebar navigasyonu + sağ tarafta içerik alanı.
 * Mobilde Sheet (drawer) ile açılır sidebar.
 * Renk teması: koyu kırmızı (admin vurgusu için)
 */
const AdminLayout = ({ children }) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const location = useLocation();

  const pageTitle = getPageTitle(location.pathname);
  const breadcrumbs = getBreadcrumbs(location.pathname);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* ============ Mobil Üst Bar ============ */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSheetOpen(true)}
          aria-label="Menüyü aç"
        >
          <Menu size={24} />
        </Button>
        <h1 className="text-lg font-semibold text-[#0A192F]">{pageTitle}</h1>
        <div className="w-9" />
      </div>

      {/* ============ Mobil Sidebar (Sheet) ============ */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="left" className="w-64 p-0 bg-[#1a1a2e] border-r-0">
          <SheetHeader className="sr-only">
            <SheetTitle>Admin Panel Navigasyonu</SheetTitle>
            <SheetDescription>Admin panel menü seçenekleri</SheetDescription>
          </SheetHeader>
          <SidebarNav onItemClick={() => setSheetOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* ============ Desktop Sidebar ============ */}
      <aside className="hidden lg:flex fixed top-0 left-0 h-full w-64 bg-[#1a1a2e] text-white z-40 flex-col">
        <SidebarNav onItemClick={() => {}} />
      </aside>

      {/* ============ Ana İçerik ============ */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        {/* Üst Bar: Breadcrumb + Sayfa Başlığı */}
        <div className="hidden lg:block border-b border-gray-200 bg-white px-8 py-4">
          <nav className="flex items-center text-sm text-muted-foreground mb-1">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.path}>
                {index > 0 && <ChevronRight size={14} className="mx-1.5 text-gray-400" />}
                {index === breadcrumbs.length - 1 ? (
                  <span className="text-foreground font-medium">{crumb.label}</span>
                ) : (
                  <Link to={crumb.path} className="hover:text-foreground transition-colors">
                    {crumb.label}
                  </Link>
                )}
              </React.Fragment>
            ))}
          </nav>
          <h1 className="text-2xl font-bold text-[#0A192F]">{pageTitle}</h1>
        </div>

        {/* İçerik Alanı */}
        <div className="p-6 lg:p-8">
          {children || <Outlet />}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
